import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonSpinner,
  IonText,
  IonToolbar,
  useIonModal,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react'
import { addOutline, flashlightOutline, removeOutline } from 'ionicons/icons'
import { DBR, TextResult } from 'capacitor-plugin-dynamsoft-barcode-reader'
import { CameraPreview } from 'capacitor-plugin-camera'
import { Capacitor, PluginListenerHandle } from '@capacitor/core'
import { useHistory } from 'react-router'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { AuthenticationService } from '../../login/services/authentication.service'
import { AppRouterConstants } from '../../../components/appRouter/AppRouter.constants'
import { useDispatch, useSelector } from 'react-redux'
import { getUserSelector } from '../../user/selectors'
import { DriversLicenseScannerService } from '../services/driversLicenseScanner.service'
import DriversLicenseResultsView from '../driversLicenseResultsView/DriversLicenseResultsView.component'
import { getDriversLicenseScannerSelector } from '../selectors'
import { importDriversLicenseInfo } from '../store/driversLicenseScanner.actions'
import './DriverLicenseScanner.page.css'
import { isTablet } from '../../../utilities'
import {
  LicenseManager,
  CameraView,
  CameraEnhancer,
  CaptureVisionRouter,
  MultiFrameResultCrossFilter,
  EnumBarcodeFormat,
} from 'dynamsoft-barcode-reader-bundle'
import { CodeParserModule } from 'dynamsoft-code-parser'
import { CoreModule } from 'dynamsoft-core'
import Logger from '../../../services/logger.service'

const DriversLicenseScanner: React.FC = () => {
  const user = useSelector(getUserSelector)
  const { serviceId, userId } = user
  const history = useHistory()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [torch, setTorch] = useState<boolean>(false)
  const [cameraAccess, setCameraAccess] = useState<boolean>(false)

  const interval = useRef<number>()
  const cameraEnhancer = useRef<CameraEnhancer>()
  const cvRouterRef = useRef<CaptureVisionRouter>()
  const onPlayedListener = useRef<PluginListenerHandle>()
  // decode guard/loop flags only gate an internal async loop, never rendered — refs avoid a re-render every 200ms
  const decodingRef = useRef<boolean>(false)
  const isScanningRef = useRef<boolean>(false)
  const isActiveRef = useRef<boolean>(false)

  const dlScanner = useSelector(getDriversLicenseScannerSelector)
  const driversLicenseScannerServiceRef = useRef(new DriversLicenseScannerService())

  const allowSecureRoutes = AuthenticationService.allowSecureRoutes(user)
  useEffect(() => {
    if (!allowSecureRoutes) {
      Logger.warn('User is not authenticated. Redirecting to index route.')
      history.push(AppRouterConstants.INDEX_ROUTE)
    }
  }, [allowSecureRoutes])

  useIonViewWillEnter(() => {
    Logger.info(`DriversLicenseScanner opened by user: ${userId} in service: ${serviceId}`)

    isActiveRef.current = true
    document.querySelector('body')?.classList.add('transparent-body')
    initializeScanner()
  }, [])

  useIonViewWillLeave(async () => {
    Logger.info(`DriversLicenseScanner closed by user: ${userId} in service: ${serviceId}`)

    isActiveRef.current = false
    stopDecoding()
    if (Capacitor.isNativePlatform()) {
      if (onPlayedListener.current) {
        await onPlayedListener.current.remove()
        onPlayedListener.current = undefined
      }
      await CameraPreview.stopCamera()
    } else {
      await disposeWebScanner()
    }

    driversLicenseScannerServiceRef.current = new DriversLicenseScannerService()
    document.querySelector('body')?.classList.remove('transparent-body')
    setLoadingState(false)
  })

  const disposeWebScanner = async () => {
    if (cvRouterRef.current) {
      cvRouterRef.current.stopCapturing()
      await cvRouterRef.current.dispose()
      cvRouterRef.current = undefined
    }

    if (cameraEnhancer.current) {
      if (cameraEnhancer.current.isOpen()) cameraEnhancer.current.close()
      if (cameraEnhancer.current.isTorchOn) cameraEnhancer.current.turnOffTorch()
      cameraEnhancer.current.dispose()
      cameraEnhancer.current = undefined
    }
  }

  const initializeScannerForWeb = async () => {
    await disposeWebScanner()

    CoreModule.engineResourcePaths.rootDirectory = 'https://cdn.jsdelivr.net/npm/'

    try {
      await LicenseManager.initLicense(`${process.env.REACT_APP_DYNAMSOFT_JAVASCRIPT_SDK_LICENSE}`)
    } catch (error) {
      Logger.log('DriversLicenseScanner error initiating dynamsoft license for web')
    }

    CoreModule.loadWasm(['DBR', 'DCP'])
    CodeParserModule.loadSpec('AAMVA_DL_ID')
    CodeParserModule.loadSpec('AAMVA_DL_ID_WITH_MAG_STRIPE')
    const cameraView = await CameraView.createInstance()
    cameraEnhancer.current = await CameraEnhancer.createInstance(cameraView)
    cameraEnhancer.current.setResolution({ width: 1920, height: 1080 })
    cameraEnhancer.current.singleFrameMode = 'disabled'

    const cameraViewContainer: HTMLElement | null = document.querySelector('#scanner-overlay')

    if (cameraViewContainer?.hasChildNodes()) {
      const children = cameraViewContainer.childNodes
      children.forEach((child) => cameraViewContainer.removeChild(child))
    }

    cameraViewContainer?.append(cameraView.getUIElement())
    const cvRouter = await CaptureVisionRouter.createInstance()
    cvRouterRef.current = cvRouter
    cvRouter.setInput(cameraEnhancer.current)

    const filter = new MultiFrameResultCrossFilter()
    filter.enableResultDeduplication('barcode', true)
    await cvRouter.addResultFilter(filter)

    cvRouter.addResultReceiver({
      onDecodedBarcodesReceived: async (result) => {
        if (!result.barcodeResultItems.length) {
          Logger.debug('initializeScannerForWeb scanned barcode received contains no data')
          return
        }
        const resultText = result.barcodeResultItems[0].text
        if (resultText.length) {
          saveResults(resultText)
        }
      },
    })

    const settings = await cvRouter.getSimplifiedSettings('ReadDenseBarcodes')
    settings.barcodeSettings.barcodeFormatIds = EnumBarcodeFormat.BF_PDF417
    settings.barcodeSettings.localizationModes = [16, 2, 0, 0, 0, 0, 0, 0]
    settings.barcodeSettings.expectedBarcodesCount = 1
    await cvRouter.updateSettings('ReadDenseBarcodes', settings)

    return {
      cameraView,
      cvRouter,
    }
  }

  const initializeScanner = async () => {
    setLoadingState(true)

    if (Capacitor.isNativePlatform()) {
      try {
        await CameraPreview.initialize()
        await DBR.initLicense({
          license: `${process.env.REACT_APP_DYNAMSOFT_BARCODE_READER_LICENSE}`,
        })
        await DBR.initialize()

        if (!isActiveRef.current) return

        if (onPlayedListener.current) {
          await onPlayedListener.current.remove()
        }

        onPlayedListener.current = await CameraPreview.addListener('onPlayed', async (res) => {
          startDecoding()
        })

        await CameraPreview.requestCameraPermission()

        if (!isActiveRef.current) {
          if (onPlayedListener.current) {
            await onPlayedListener.current.remove()
            onPlayedListener.current = undefined
          }
          return
        }

        await CameraPreview.startCamera()

        const orientation = await CameraPreview.getOrientation()
        const resolution = await CameraPreview.getResolution()
        const selectedCamera = await CameraPreview.getSelectedCamera()
        const allCameras = await CameraPreview.getAllCameras()

        Logger.info(`DriversLicenseScanner - CameraPreview Details:`)
        Logger.info(`  Orientation: ${orientation?.orientation}`)
        Logger.info(`  Resolution: ${resolution?.resolution}`)
        Logger.info(`  Selected Camera: ${selectedCamera?.selectedCamera}`)
        Logger.info(`  All Cameras: ${allCameras?.cameras}`)

        Logger.info('DriversLicenseScanner successfully initialized.')
      } catch (error) {
        Logger.info(`DriversLicenseScanner error initializing the plugin: ${error}`)
      } finally {
        setLoadingState(false)
      }
    } else {
      try {
        const init = await initializeScannerForWeb()

        if (!isActiveRef.current) {
          await disposeWebScanner()
          return
        }

        await scanWithVideoCamera(init)
      } catch (error) {
        Logger.error(`DriversLicenseScanner error initializing the scanner for web: ${error}`)
      } finally {
        setLoadingState(false)
      }
    }
  }

  const scanWithVideoCamera = async (init: { cameraView: CameraView; cvRouter: CaptureVisionRouter }) => {
    init.cvRouter?.stopCapturing()
    if (cameraEnhancer.current?.isOpen()) {
      cameraEnhancer.current.close()
    }
    init.cameraView.setScanLaserVisible(true)

    try {
      await cameraEnhancer.current?.open().then(async () => {
        if (!isActiveRef.current) {
          await disposeWebScanner()
          return
        }

        setCameraAccess(true)
        await init.cvRouter?.startCapturing('ReadDenseBarcodes')
      })
    } catch (error) {
      Logger.error(`DriversLicenseScanner error unable to open camera: ${error}`)
      setCameraAccess(false)
      init.cameraView.setScanLaserVisible(false)
    }
  }

  const setLoadingState = (loadingState: boolean) => {
    if (loadingState) {
      document.querySelector('.scanner-content')?.classList.add('scanner-visibility-hidden')
      setIsLoading(true)
    } else {
      document.querySelector('.scanner-content')?.classList.remove('scanner-visibility-hidden')
      setIsLoading(false)
    }
  }

  const onClose = () => {
    dismiss()
    dispatch(importDriversLicenseInfo())
    onScannerClose()
  }

  const onScannerClose = async () => {
    stopDecoding()
    setLoadingState(true)

    if (Capacitor.isNativePlatform()) {
      await CameraPreview.stopCamera()
    } else {
      if (cameraEnhancer.current?.isOpen()) {
        cameraEnhancer.current.close()
      }
      if (cameraEnhancer.current?.isTorchOn) cameraEnhancer.current.turnOffTorch()
    }

    if (isTablet()) {
      history.push(TabConstants.PATIENT_INFO_ROUTE)
    } else {
      history.push(TabConstants.PATIENT_DEMOGRAPHICS)
    }
  }

  const onRetry = () => {
    dismiss()
    history.push(TabConstants.ATTACHMENT_TAB)
  }

  const [present, dismiss] = useIonModal(DriversLicenseResultsView, {
    dlScanner,
    driversLicenseScannerService: driversLicenseScannerServiceRef.current,
    onClose,
    onRetry,
  })

  const stopScan = async () => {
    stopDecoding()
    await CameraPreview.stopCamera()
  }

  const saveResults = (barcodeText: string) => {
    driversLicenseScannerServiceRef.current.onUnduplicatedRead(dispatch, present, barcodeText)
  }

  const startDecoding = () => {
    stopDecoding()
    Logger.info(`DriversLicenseScanner - startDecoding > decoding: ${decodingRef.current}`)
    isScanningRef.current = true
    scheduleNextDecode()
  }

  // self-scheduling so the next capture only starts once the previous decode has actually finished
  const scheduleNextDecode = () => {
    interval.current = window.setTimeout(async () => {
      await captureAndDecode()
      if (isScanningRef.current) {
        scheduleNextDecode()
      }
    }, 200)
  }

  const stopDecoding = () => {
    isScanningRef.current = false
    clearTimeout(interval.current)
  }

  const captureAndDecode = async () => {
    if (decodingRef.current || !Capacitor.isNativePlatform()) {
      return
    }

    let results: TextResult[] = []
    let barcodeText: TextResult | null = null
    decodingRef.current = true

    try {
      await CameraPreview.saveFrame()
      results = (await DBR.decodeBitmap({})).results

      if (results.length > 0) {
        Logger.info(
          `DriversLicenseScanner - Number of results from (await DBR.decodeBitmap({})).results: ${results.length}`
        )

        barcodeText = results[0]
        const barcodeFormat = barcodeText.barcodeFormat?.toUpperCase() ?? ''
        Logger.info(`DriversLicenseScanner - Barcode Format: ${barcodeFormat}`)

        if (barcodeFormat === 'PDF417') {
          saveResults(barcodeText.barcodeText)
          stopScan()
        }
      }
    } catch (error) {
      Logger.error(`captureAndDecode() error: ${error}`)
    }
    decodingRef.current = false
  }

  const zoomIn = () => {
    if (Capacitor.isNativePlatform()) {
      CameraPreview.setZoom({ factor: 2.5 })
    } else {
      cameraEnhancer.current?.setZoom({ factor: 2.5 })
    }
    Logger.info('DriversLicenseScanner - Zoom In')
  }

  const zoomOut = () => {
    if (Capacitor.isNativePlatform()) {
      CameraPreview.setZoom({ factor: 1 })
    } else {
      cameraEnhancer.current?.setZoom({ factor: 1 })
    }
    Logger.info('DriversLicenseScanner - Zoom Out')
  }

  const toggleTorch = () => {
    const torchStatus = !torch
    if (Capacitor.isNativePlatform()) {
      CameraPreview.toggleTorch({ on: torchStatus })
    } else {
      if (torchStatus) {
        cameraEnhancer.current?.turnOnTorch()
      } else {
        cameraEnhancer.current?.turnOffTorch()
      }
    }
    setTorch(torchStatus)
    Logger.info(`DriversLicenseScanner - torch toggled to: ${torchStatus}`)
  }

  const renderHeader = () => {
    return (
      <IonHeader className='scanner-header'>
        <div className='camera-preview-cover' />
        <IonToolbar color='light' className='ion-align-items-center ion-justify-content-between scanner-toolbar'>
          <IonButtons slot='start'>
            <IonButton qa-id='modal_close_button' color='primary-alt' onClick={onScannerClose}>
              {t('common.buttons.Close')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton qa-id='zoomIn' color='primary-alt' onClick={() => zoomIn()}>
              <IonIcon slot='icon-only' icon={addOutline} />
            </IonButton>
            <IonButton qa-id='zoomOut' color='primary-alt' onClick={() => zoomOut()}>
              <IonIcon slot='icon-only' icon={removeOutline} />
            </IonButton>
            <IonButton qa-id='torchButton' color='primary-alt' onClick={() => toggleTorch()}>
              <IonIcon slot='icon-only' icon={flashlightOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
    )
  }

  return (
    <IonPage id='DriversLicenseScannerPage' data-testid='DriversLicenseScanner'>
      {renderHeader()}
      <IonContent fullscreen class='scanner-content' scrollX={false} scrollY={false}>
        <div className='scanner-animation' />
        <div className='scanner-overlay-container'>
          <div className='scanner-overlay' id='scanner-overlay' />
          {!Capacitor.isNativePlatform() && !cameraAccess ? (
            <IonText className='scanner-help-message'>{t('dl.CameraAccessError')}</IonText>
          ) : (
            <IonText className='scanner-help-message'>{t('dl.ScanBarcodeOnBackOfTheLicense')}</IonText>
          )}
        </div>
      </IonContent>
      {isLoading && <IonSpinner className='scanner-loader' />}
    </IonPage>
  )
}

export default DriversLicenseScanner
